import { BossEncounterContent, ContentRegistry, EnemyContent, GameSettingsContent, loadContentRegistry } from '../data';
import { EventBus } from '../core/EventBus';
import { ISceneAdapter } from '../engine/ISceneAdapter';
import { EnemyFactory } from '../factories/EnemyFactory';
import { createLogger } from '../utils/Logger';
import {
  applyRuntimeTestingOverrides,
  DEFAULT_RUNTIME_TESTING_MODE_STATE,
  RuntimeTestingModeState
} from '../config/RuntimeTestingMode';
import { assignBossEncounterMetadata, createBossEncounterEventMetadata, BossEncounterResolvedStats } from './BossEventPayloads';
import { EnemyResolvedStats } from './EnemyScalingResolver';

type BossPauseCallback = (isBossActive: boolean, state: { bossesSpawned: number }) => void;

export class BossEncounterSystem {
  private readonly enemyFactory: EnemyFactory;
  private readonly eventBus: EventBus;
  private readonly sceneAdapter: ISceneAdapter;
  private readonly contentRegistry: ContentRegistry;
  private readonly onBossPauseChanged: BossPauseCallback;
  private readonly logger = createLogger('BossEncounterSystem');
  private readonly eventUnsubscribers: Array<() => void> = [];

  private baseGameSettings: GameSettingsContent;
  private effectiveGameSettings: GameSettingsContent;
  private runtimeTestingMode: RuntimeTestingModeState = DEFAULT_RUNTIME_TESTING_MODE_STATE;
  private elapsedTimeSeconds = 0;
  private nextEncounterIndex = 0;
  private activeEncounter: BossEncounterContent | null = null;
  private bossesSpawned = 0;
  private bossesKilled = 0;

  constructor(
    enemyFactory: EnemyFactory,
    eventBus: EventBus,
    sceneAdapter: ISceneAdapter,
    contentRegistry: ContentRegistry = loadContentRegistry(),
    runtimeTestingMode: RuntimeTestingModeState = DEFAULT_RUNTIME_TESTING_MODE_STATE,
    onBossPauseChanged: BossPauseCallback = () => undefined
  ) {
    this.enemyFactory = enemyFactory;
    this.eventBus = eventBus;
    this.sceneAdapter = sceneAdapter;
    this.contentRegistry = contentRegistry;
    this.onBossPauseChanged = onBossPauseChanged;
    this.baseGameSettings = this.contentRegistry.getGameSettings();
    this.runtimeTestingMode = runtimeTestingMode;
    this.effectiveGameSettings = applyRuntimeTestingOverrides(this.baseGameSettings, this.runtimeTestingMode, this.logger);

    this.subscribeEvents();
    this.logger.info('constructor:ready', {
      encounterCount: this.getEncounters().length,
      runtimeTestingMode: this.runtimeTestingMode
    });
  }

  public setRuntimeTestingMode(runtimeTestingMode: RuntimeTestingModeState): void {
    if (!runtimeTestingMode) {
      this.logger.warn('setRuntimeTestingMode:invalid-state', { runtimeTestingMode });
      return;
    }

    this.runtimeTestingMode = runtimeTestingMode;
    this.effectiveGameSettings = applyRuntimeTestingOverrides(this.baseGameSettings, runtimeTestingMode, this.logger);
    this.logger.info('setRuntimeTestingMode:applied', {
      enabled: runtimeTestingMode.enabled,
      earlyBoss: runtimeTestingMode.earlyBoss,
      pauseWaves: runtimeTestingMode.pauseWaves,
      bossSpawnTime: this.effectiveGameSettings.bossSpawnTime,
      bossesToWin: this.effectiveGameSettings.bossesToWin
    });
  }

  public update(dtSeconds: number): void {
    if (this.runtimeTestingMode.enabled && this.runtimeTestingMode.pauseWaves) {
      return;
    }

    if (!Number.isFinite(dtSeconds) || dtSeconds <= 0) {
      return;
    }

    this.elapsedTimeSeconds += dtSeconds;
    const encounters = this.getEncounters();
    const nextEncounter = encounters[this.nextEncounterIndex];
    if (!nextEncounter || this.activeEncounter) {
      return;
    }

    if (this.elapsedTimeSeconds >= nextEncounter.spawnTime) {
      this.spawnEncounter(nextEncounter);
    }
  }

  public forceSpawnNextEncounterForTesting(): {
    spawned: boolean;
    encounterId?: string;
    reason?: 'active-encounter' | 'no-remaining-encounters';
  } {
    if (this.activeEncounter) {
      return { spawned: false, reason: 'active-encounter' };
    }

    const encounters = this.getEncounters();
    const nextEncounter = encounters[this.nextEncounterIndex];
    if (!nextEncounter) {
      return { spawned: false, reason: 'no-remaining-encounters' };
    }

    this.spawnEncounter(nextEncounter);
    return { spawned: true, encounterId: nextEncounter.id };
  }

  public dispose(): void {
    if (this.eventUnsubscribers.length === 0) {
      this.logger.warn('dispose:already-disposed');
      return;
    }

    this.eventUnsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.logger.debug('dispose:complete');
  }

  public getDiagnosticsSnapshot(): {
    elapsedTimeSeconds: number;
    activeEncounterId: string | null;
    nextEncounterId: string | null;
    bossesSpawned: number;
    bossesKilled: number;
  } {
    const encounters = this.getEncounters();
    return {
      elapsedTimeSeconds: this.elapsedTimeSeconds,
      activeEncounterId: this.activeEncounter?.id ?? null,
      nextEncounterId: encounters[this.nextEncounterIndex]?.id ?? null,
      bossesSpawned: this.bossesSpawned,
      bossesKilled: this.bossesKilled
    };
  }

  private subscribeEvents(): void {
    const onBossKilled = (event: { data?: { bossEncounter?: { encounterId?: string } } }): void => {
      const activeEncounter = this.activeEncounter;
      if (!activeEncounter) {
        this.logger.warn('boss:killed-without-active-encounter');
        return;
      }

      const killedEncounterId = event?.data?.bossEncounter?.encounterId;
      if (killedEncounterId && killedEncounterId !== activeEncounter.id) {
        this.logger.warn('boss:killed-encounter-mismatch', {
          activeEncounterId: activeEncounter.id,
          killedEncounterId
        });
        return;
      }

      this.bossesKilled += 1;
      this.activeEncounter = null;
      this.onBossPauseChanged(false, { bossesSpawned: this.bossesSpawned });
      this.logger.info('encounter:killed', {
        encounterId: killedEncounterId ?? activeEncounter.id,
        bossesSpawned: this.bossesSpawned,
        bossesKilled: this.bossesKilled
      });
    };

    this.eventBus.on('boss:killed', onBossKilled);
    this.eventUnsubscribers.push(() => this.eventBus.off('boss:killed', onBossKilled));
  }

  private getEncounters(): readonly BossEncounterContent[] {
    return this.effectiveGameSettings.bossEncounters ?? [];
  }

  private spawnEncounter(encounter: BossEncounterContent): void {
    const position = this.resolveSpawnPosition();
    const enemyContent = this.contentRegistry.findEnemy(encounter.enemyId);
    if (!enemyContent) {
      this.logger.warn('spawnEncounter:missing-enemy-content', {
        encounterId: encounter.id,
        enemyId: encounter.enemyId
      });
      this.nextEncounterIndex += 1;
      return;
    }

    const resolvedStats = this.resolveEncounterStats(enemyContent, encounter);
    this.logger.debug('spawnEncounter:resolved-stats', {
      encounterId: encounter.id,
      enemyId: encounter.enemyId,
      resolvedStats
    });
    this.logger.info('spawnEncounter:applied-modifiers', {
      encounterId: encounter.id,
      hpMultiplier: encounter.hpMultiplier ?? 1,
      damageMultiplier: encounter.statModifiers?.damageMultiplier ?? 1,
      speedMultiplier: encounter.statModifiers?.speedMultiplier ?? 1,
      xpRewardMultiplier: encounter.statModifiers?.xpRewardMultiplier ?? 1
    });
    const enemy = this.enemyFactory.create(encounter.enemyId, position.x, position.y, { resolvedStats });
    if (!enemy) {
      this.logger.warn('spawnEncounter:enemy-factory-returned-null', {
        encounterId: encounter.id,
        enemyId: encounter.enemyId
      });
      this.nextEncounterIndex += 1;
      return;
    }

    const metadata = createBossEncounterEventMetadata(
      encounter,
      resolvedStats,
      {
        enemyName: enemyContent.name,
        enemyNameKey: (enemyContent as EnemyContent & { nameKey?: string }).nameKey
      }
    );
    const sprite = (enemy as unknown as { getSprite?: () => { setData?: (key: string, value: unknown) => unknown } }).getSprite?.();
    if (sprite) {
      assignBossEncounterMetadata(sprite, metadata);
    }

    this.activeEncounter = encounter;
    this.nextEncounterIndex += 1;
    this.bossesSpawned += 1;
    this.onBossPauseChanged(true, { bossesSpawned: this.bossesSpawned });

    this.eventBus.publish({
      type: 'boss:spawned',
      data: {
        boss: enemy,
        bossEncounter: metadata
      }
    });

    this.logger.info('encounter:spawned', {
      encounterId: encounter.id,
      enemyId: encounter.enemyId,
      spawnTime: encounter.spawnTime,
      x: position.x,
      y: position.y,
      bossesSpawned: this.bossesSpawned
    });
  }

  private resolveSpawnPosition(): { x: number; y: number } {
    const { width, height } = this.sceneAdapter.getSceneBounds();
    const scroll = this.sceneAdapter.getCameraScroll();
    const edge = Math.floor(Math.random() * 4);
    const padding = 100;

    if (edge === 0) {
      return { x: scroll.x + width / 2, y: scroll.y - padding };
    }
    if (edge === 1) {
      return { x: scroll.x + width + padding, y: scroll.y + height / 2 };
    }
    if (edge === 2) {
      return { x: scroll.x + width / 2, y: scroll.y + height + padding };
    }

    return { x: scroll.x - padding, y: scroll.y + height / 2 };
  }

  private resolveEncounterStats(enemy: EnemyContent, encounter: BossEncounterContent): EnemyResolvedStats & BossEncounterResolvedStats {
    const hpMultiplier = encounter.hpMultiplier ?? 1;
    const damageMultiplier = encounter.statModifiers?.damageMultiplier ?? 1;
    const speedMultiplier = encounter.statModifiers?.speedMultiplier ?? 1;
    const xpRewardMultiplier = encounter.statModifiers?.xpRewardMultiplier ?? 1;
    const maxHealth = enemy.stats.hp * hpMultiplier;
    const candidateStats = {
      health: maxHealth,
      maxHealth,
      damage: enemy.stats.damage * damageMultiplier,
      speed: enemy.stats.speed * speedMultiplier,
      xpReward: enemy.stats.xpReward * xpRewardMultiplier
    };

    const hasUnsafeValue = Object.values(candidateStats).some(value => !Number.isFinite(value) || value < 0);
    if (hasUnsafeValue) {
      this.logger.warn('resolveEncounterStats:unsafe-output-fallback', {
        encounterId: encounter.id,
        enemyId: encounter.enemyId,
        candidateStats
      });
      return {
        health: enemy.stats.hp,
        maxHealth: enemy.stats.hp,
        damage: enemy.stats.damage,
        speed: enemy.stats.speed,
        xpReward: enemy.stats.xpReward
      };
    }

    return candidateStats;
  }
}
